import { ArrowUpIcon } from "@heroicons/react/outline";
import { Editor } from "@tiptap/core";
import { FormEvent, forwardRef, useContext, useEffect, useState } from "react";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Button, { buttonTheme } from "@components/button/button";
import Dropdown from "@components/dropdown/dropdown";
import Input from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import {
  TopicflowIcon,
  TopicflowIconType,
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon";
import TextOnlyWysiwyg from "@components/wysiwyg/text-only-wysiwyg";
import { classNames } from "@helpers/css";

import { ChatSessionContext } from "../ask-ai";

const AskAIForm = forwardRef(
  (
    {
      chatSessionId,
      inputMessage,
      formButtons,
      loadingCreateMessage,
      loadingRetryAskAiMessage,
      onSubmit,
      onEditorReady,
      onChangeInputValue,
      onClickPromptSuggestion,
    }: {
      chatSessionId: number | null;
      inputMessage: string;
      loadingCreateMessage: boolean;
      loadingRetryAskAiMessage: boolean;
      onSubmit: (e?: FormEvent<HTMLFormElement>) => void;
      onEditorReady: (editor: Editor) => void;
      onChangeInputValue: (value: string) => void;
      onClickPromptSuggestion: (prompt: string) => void;
      formButtons?: {
        text: string;
        prompt: string;
        icon: TopicflowIconType;
      }[];
    },
    ref: React.Ref<HTMLFormElement>
  ) => {
    const { meetingId } = useContext(ChatSessionContext);
    const [newPrompt, setNewPrompt] = useState("");
    const [isAddCustomPromptModalOpen, setIsAddCustomPromptModalOpen] =
      useState(false);
    const buttonClassName = classNames(
      "rounded-full p-1.5 bg-gray-100 text-gray-500",
      inputMessage.trim().length > 0 &&
        "bg-blue-link text-white hover:bg-blue-800"
    );
    const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

    useEffect(() => {
      if (window.Intercom) {
        window.Intercom("update", { hide_default_launcher: true });
      }
      return function cleanup() {
        if (window.Intercom) {
          window.Intercom("update", { hide_default_launcher: false });
        }
      };
    }, []);

    const customPromptOptions = (
      uiPreferenceCache.askAiCustomPrompts || []
    ).map((prompt) => ({
      type: "button" as const,
      label: prompt,
      labelClassName: "line-clamp-2 max-w-96",
      onClick: () => onClickPromptSuggestion(prompt),
    }));

    const handleSaveNewPrompt = () => {
      if (newPrompt) {
        const askAiCustomPrompts = uiPreferenceCache.askAiCustomPrompts || [];
        saveUiPreference({
          askAiCustomPrompts: [...askAiCustomPrompts, newPrompt.trim()],
        });
        setNewPrompt("");
      }
    };

    const handleDeleteCustomPrompt = (prompt: string) => {
      const askAiCustomPrompts = uiPreferenceCache.askAiCustomPrompts || [];
      saveUiPreference({
        askAiCustomPrompts: askAiCustomPrompts.filter((p) => p !== prompt),
      });
      setNewPrompt("");
    };

    const handleAddCustomPrompt = () => {
      setIsAddCustomPromptModalOpen(true);
    };

    const allPromptOptions = [
      ...customPromptOptions,
      {
        type: "divider" as const,
      },
      {
        type: "button" as const,
        label: "Manage Prompts",
        onClick: handleAddCustomPrompt,
      },
    ];

    const handleCloseAddCustomPromptModal = () => {
      setIsAddCustomPromptModalOpen(false);
      setNewPrompt("");
    };

    return (
      <form
        className={classNames("border rounded-xl bg-white")}
        onSubmit={onSubmit}
        ref={ref}
      >
        <TextOnlyWysiwyg
          key={chatSessionId}
          value={inputMessage}
          onChangeValue={onChangeInputValue}
          placeholder={
            meetingId ? "Ask anything about this meeting..." : "Ask anything..."
          }
          className={classNames(
            "js-ask-ai-input",
            "w-full border-none focus:ring-0 focus:outline-0 p-4",
            "pb-1 min-h-16 max-h-48 overflow-y-auto"
          )}
          editable
          onEnterPressed={onSubmit}
          onEditorReady={onEditorReady}
        />
        <div className="flex justify-between items-center px-4 pb-4">
          <div className="flex items-center gap-2">
            {formButtons &&
              formButtons.map((formButton) => (
                <Button
                  key={formButton.text}
                  onClick={() => {
                    onClickPromptSuggestion(formButton.prompt);
                  }}
                >
                  <TopicflowIcon type="users" className="h-4 w-4" />
                  {formButton.text}
                </Button>
              ))}
            {customPromptOptions.length > 0 ? (
              <Dropdown options={allPromptOptions} />
            ) : (
              <Button
                icon={getTopicflowIcon("plus")}
                onClick={handleAddCustomPrompt}
                theme={buttonTheme.iconGray}
              />
            )}
          </div>
          <button
            type="submit"
            disabled={
              inputMessage.trim().length === 0 ||
              loadingCreateMessage ||
              loadingRetryAskAiMessage
            }
            className={buttonClassName}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
        </div>
        <Modal
          alignedTop
          open={isAddCustomPromptModalOpen}
          onClose={handleCloseAddCustomPromptModal}
        >
          <div className="p-6 flex flex-col gap-6">
            <ModalTitle onClose={handleCloseAddCustomPromptModal}>
              Custom Prompts
            </ModalTitle>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                placeholder="New Prompt..."
                onChange={(e) => setNewPrompt(e.target.value)}
                value={newPrompt}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveNewPrompt();
                  }
                }}
                maxLength={200}
              />
              <Button
                onClick={handleSaveNewPrompt}
                text="Add"
                disabled={newPrompt.trim().length === 0}
              />
            </div>
            <div className="flex flex-col divide-y">
              {(uiPreferenceCache.askAiCustomPrompts || []).map((prompt) => (
                <div key={prompt}>
                  <div
                    className="flex justify-between px-2 py-1 hover:bg-gray-50"
                    role="button"
                    onClick={() => {
                      onClickPromptSuggestion(prompt);
                      handleCloseAddCustomPromptModal();
                    }}
                  >
                    <div className="flex-1 flex items-center">{prompt}</div>
                    <Button
                      icon={getTopicflowIcon("trash")}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteCustomPrompt(prompt);
                      }}
                      theme={buttonTheme.iconGray}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Modal>
      </form>
    );
  }
);

export default AskAIForm;
